/**
 * SMS Verification Component
 *
 * 전화번호 SMS 인증을 처리하는 컴포넌트
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import smsService, { SmsPurpose } from '../services/sms';

interface SMSVerificationProps {
  phoneNumber: string;
  // 인증 용도 — 가입은 'register'(user_id 미전송) [C-16][C-32]
  purpose?: SmsPurpose;
  // 로그인 재인증 서명 챌린지 (login 용도) [C-20]
  smsChallenge?: string;
  onVerified: (response?: any) => void; // SMS 인증 응답 데이터 전달 (register 는 verificationId 포함)
  onError?: (error: string) => void;
}

// Styled Components
const VerificationContainer = styled.div`
  width: 100%;
  margin: 1rem 0;
  padding: 1.5rem;
  border: 1px solid var(--ws-line);
  border-radius: 8px;
  background-color: #f9f9f9;
`;

const Title = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: var(--ws-ink);
  font-weight: 600;
`;

const InfoText = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  color: var(--ws-ink-3);
  line-height: 1.5;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Input = styled.input.withConfig({
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError?: boolean }>`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid ${(props) => (props.hasError ? '#e53e3e' : '#ddd')};
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  text-align: center;
  letter-spacing: 0.3em;
  font-weight: 600;
  font-size: 1.2rem;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? 'var(--ws-red)' : 'var(--ws-blue)')};
    box-shadow: 0 0 0 2px
      ${(props) => (props.hasError ? 'rgba(229, 62, 62, 0.2)' : 'rgba(74, 108, 250, 0.2)')};
  }

  &::placeholder {
    letter-spacing: normal;
    font-weight: normal;
    font-size: 0.9rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  ${(props) =>
    props.variant === 'secondary'
      ? `
    background-color: var(--ws-sunken);
    color: var(--ws-ink);
    border: 1px solid #ddd;

    &:hover:not(:disabled) {
      background-color: var(--ws-line);
    }
  `
      : `
    background-color: var(--ws-blue);
    color: white;

    &:hover:not(:disabled) {
      background-color: #3a5cf5;
    }
  `}

  &:disabled {
    background-color: var(--ws-line-strong);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ErrorMessage = styled.div`
  color: var(--ws-red);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:before {
    content: '⚠️';
  }
`;

const SuccessMessage = styled.div`
  color: var(--ws-green);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:before {
    content: '✓';
    font-weight: bold;
  }
`;

const TimerText = styled.span`
  color: var(--ws-blue);
  font-weight: 600;
  margin-left: 0.5rem;
`;

const SMSVerification: React.FC<SMSVerificationProps> = ({
  phoneNumber,
  purpose,
  smsChallenge,
  onVerified,
  onError,
}) => {
  // send/verify/resend 공통 옵션 (user_id 미전송) [C-16]
  const smsOptions = { purpose, smsChallenge };
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // 카운트다운 타이머
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // phoneNumber / purpose / smsChallenge 가 바뀌면 이전 인증 진행 상태를 초기화 [Q2-5]
  //  - 전화번호 변경 후에도 이전 코드 입력/발송/인증완료 표시가 stale 로 남는 문제 방지
  useEffect(() => {
    setCode('');
    setError(null);
    setIsLoading(false);
    setIsSending(false);
    setCodeSent(false);
    setIsVerified(false);
    setCountdown(0);
  }, [phoneNumber, purpose, smsChallenge]);

  // 인증번호 발송
  const handleSendCode = async () => {
    if (!phoneNumber) {
      setError('전화번호를 입력해주세요.');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await smsService.sendVerificationCode(phoneNumber, smsOptions);

      if (response.success) {
        setCodeSent(true);
        setCountdown(300); // 5분
        setError(null);
      } else {
        const errorMessage = response.error?.message || '인증번호 발송에 실패했습니다.';
        setError(errorMessage);
        if (onError) onError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || '인증번호 발송에 실패했습니다.';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  // 인증번호 재발송
  const handleResendCode = async () => {
    setIsSending(true);
    setError(null);

    try {
      const response = await smsService.resendCode(phoneNumber, smsOptions);

      if (response.success) {
        setCountdown(300); // 5분
        setError(null);
        setCode(''); // 입력 필드 초기화
      } else {
        const errorMessage = response.error?.message || '인증번호 재발송에 실패했습니다.';
        setError(errorMessage);
        if (onError) onError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || '인증번호 재발송에 실패했습니다.';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  // 인증번호 확인
  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError('6자리 인증번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await smsService.verifyCode(phoneNumber, code, smsOptions);

      if (response.success && response.data?.verified) {
        setIsVerified(true);
        setError(null);

        // JWT 토큰이 포함된 경우 localStorage에 저장 (로그인 재인증 처리)
        if (response.token && response.user) {
          // 토큰 저장
          localStorage.setItem('auth_token', response.token);

          // Refresh Token 저장 (있는 경우)
          if (response.refreshToken) {
            localStorage.setItem('refresh_token', response.refreshToken);
          }

          // 사용자 정보 저장
          localStorage.setItem('user_info', JSON.stringify(response.user));

          // 세션 만료 시간 설정
          const expiresIn = response.expiresIn || 3600; // Default: 1 hour
          const sessionExpiry = new Date(Date.now() + expiresIn * 1000);
          localStorage.setItem('session_expiry', sessionExpiry.toISOString());
        }

        setTimeout(() => {
          // ✅ SMS 인증 응답 데이터를 LoginPage로 전달 (토큰 정보 포함)
          onVerified(response);
        }, 1000);
      } else {
        const errorMessage = response.error?.message || '인증번호가 일치하지 않습니다.';
        setError(errorMessage);
        if (onError) onError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || '인증 처리에 실패했습니다.';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 인증번호 입력 핸들러 (숫자만 허용)
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  // Enter 키 처리
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleVerifyCode();
    }
  };

  // 카운트다운 표시 형식 (MM:SS)
  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <VerificationContainer>
      <Title>📱 전화번호 인증</Title>

      {!codeSent ? (
        <>
          <InfoText>
            입력하신 전화번호({phoneNumber})로 인증번호를 발송합니다.
          </InfoText>
          <Button
            variant="primary"
            onClick={handleSendCode}
            disabled={isSending}
          >
            {isSending ? '발송 중...' : '인증번호 발송'}
          </Button>
        </>
      ) : isVerified ? (
        <SuccessMessage>전화번호 인증이 완료되었습니다!</SuccessMessage>
      ) : (
        <>
          <InfoText>
            {phoneNumber}로 전송된 6자리 인증번호를 입력해주세요.
            {countdown > 0 && <TimerText>{formatCountdown(countdown)}</TimerText>}
          </InfoText>

          <InputContainer>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={handleCodeChange}
              onKeyPress={handleKeyPress}
              hasError={!!error}
              disabled={isVerified}
              autoFocus
            />
          </InputContainer>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleVerifyCode}
              disabled={isLoading || code.length !== 6 || isVerified}
            >
              {isLoading ? '확인 중...' : '인증 확인'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleResendCode}
              disabled={isSending || countdown > 240} // 1분 후부터 재발송 가능
            >
              {isSending ? '발송 중...' : '재발송'}
            </Button>
          </ButtonGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </>
      )}
    </VerificationContainer>
  );
};

export default SMSVerification;
